import { useEffect, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { parseLeadCsv, type LeadCsvResult } from "@/lib/lead-csv";
import { useImportLeadRows, type ImportResult } from "@/hooks/use-lead-list-actions";

interface CsvListFieldsProps {
  onParsed: (result: LeadCsvResult | null) => void;
  parsed: LeadCsvResult | null;
}

/** Filopplasting, forhåndsvisning av fem rader og tellinger (nye, allerede kunde, duplikater) før import. */
export default function CsvListFields({ onParsed, parsed }: CsvListFieldsProps) {
  const [fileName, setFileName] = useState<string | null>(null);
  const [preview, setPreview] = useState<ImportResult | null>(null);
  const dryRun = useImportLeadRows();

  useEffect(() => {
    setPreview(null);
    if (!parsed || parsed.rows.length === 0) return;
    dryRun.mutate(
      { listId: null, rows: parsed.rows, dryRun: true },
      { onSuccess: setPreview },
    );
    // Kjør bare når filen er lest på nytt.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parsed]);

  const onFile = async (file: File | undefined) => {
    if (!file) {
      setFileName(null);
      onParsed(null);
      return;
    }
    setFileName(file.name);
    onParsed(parseLeadCsv(await file.text()));
  };

  return (
    <div className="space-y-3">
      <div className="space-y-1.5">
        <Label htmlFor="lead-csv-file">CSV-fil</Label>
        <Input id="lead-csv-file" type="file" accept=".csv,text/csv" onChange={(e) => onFile(e.target.files?.[0])} />
        <p className="text-xs text-muted-foreground">
          Kolonner: orgnr, navn (påkrevd), bransjekode, bransje, ansatte, omsetning, nettside, kommune, kontakt_navn, kontakt_tittel, kontakt_epost, kontakt_mobil. Skilletegn ; eller , og maks 2000 rader.
        </p>
      </div>

      {parsed && (
        <div className="space-y-2 text-sm" aria-live="polite">
          <p>
            {fileName}: {parsed.rows.length} gyldige rader
            {parsed.errors.length > 0 ? `, ${parsed.errors.length} med feil` : ""}.
          </p>
          {preview && (
            <p className="font-medium">
              {preview.inserted} nye, {preview.skipped_customer} allerede kunde, {preview.skipped_duplicate} duplikater
            </p>
          )}
          {parsed.errors.length > 0 && (
            <ul className="max-h-28 overflow-y-auto border border-border p-2 text-xs">
              {parsed.errors.slice(0, 20).map((e) => (
                <li key={`${e.line}-${e.message}`}>
                  {e.line > 0 ? `Linje ${e.line}: ` : ""}
                  {e.message}
                </li>
              ))}
            </ul>
          )}
          {parsed.rows.length > 0 && (
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-muted-foreground">
                  <th className="pr-3">Navn</th>
                  <th className="pr-3">Org.nr</th>
                  <th>Kontakt</th>
                </tr>
              </thead>
              <tbody>
                {parsed.rows.slice(0, 5).map((r, i) => (
                  <tr key={`${r.name}-${i}`}>
                    <td className="pr-3">{r.name}</td>
                    <td className="pr-3">{r.organization_number ?? "–"}</td>
                    <td>{r.contacts[0]?.full_name ?? "–"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}
